/**
 * Publishing Administration — list, republish, invalidate, rebuild, unpublish
 */
import React, { useState, useCallback } from "react";
import { publishing as pubApi } from "../api.js";
import { useAsync, useDebounce } from "../hooks.js";

type ActionType = "republish" | "invalidate" | "rebuild" | "unpublish";

function deployBadge(status: string) {
  const map: Record<string, string> = {
    live: "ops-badge-green", deploying: "ops-badge-blue",
    failed: "ops-badge-red", unpublished: "ops-badge-neutral",
  };
  return <span className={`ops-badge ${map[status] ?? "ops-badge-neutral"}`}>{status}</span>;
}

export function PublishingView() {
  const [search, setSearch]   = useState("");
  const [page, setPage]       = useState(1);
  const [confirm, setConfirm] = useState<{ tenantId: string; slug: string; action: ActionType } | null>(null);
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState<string | null>(null);
  const [toast, setToast]     = useState<string | null>(null);

  const dq = useDebounce(search, 350);
  const fetcher = useCallback(
    () => pubApi.list({ q: dq, page: String(page), pageSize: "50" }),
    [dq, page]
  );
  const { data, loading, error, reload } = useAsync<any>(fetcher, [dq, page]);

  async function doAction() {
    if (!confirm) return;
    setBusy(true); setErr(null);
    try {
      if (confirm.action === "republish")  await pubApi.republish(confirm.tenantId);
      if (confirm.action === "invalidate") await pubApi.invalidate(confirm.tenantId);
      if (confirm.action === "rebuild")    await pubApi.rebuild(confirm.tenantId);
      if (confirm.action === "unpublish")  await pubApi.unpublish(confirm.tenantId);
      setToast(`${confirm.action} triggered for ${confirm.slug}`);
      setTimeout(() => setToast(null), 4000);
      setConfirm(null); reload();
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  }

  const rows: any[] = data?.items ?? [];

  return (
    <div className="ops-page">
      <div className="ops-page-header">
        <div>
          <h1 className="ops-page-title">Publishing</h1>
          {data && <p className="ops-page-subtitle">{data.total} published sites</p>}
        </div>
      </div>

      {toast && (
        <div style={{ background: "rgba(34,197,94,0.1)", border: "1px solid var(--ops-success)",
          borderRadius: "var(--ops-radius)", padding: "0.625rem 1rem", marginBottom: "1rem",
          color: "var(--ops-success)", fontSize: "0.8125rem" }}>
          ✓ {toast}
        </div>
      )}
      {error && <div className="ops-error">{error}</div>}

      <div className="ops-filters">
        <input className="ops-input" placeholder="Filter by slug or name…"
          value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        <button className="ops-btn ops-btn-secondary ops-btn-sm" onClick={reload}>↻ Refresh</button>
      </div>

      {loading && <div className="ops-loading">Loading…</div>}
      {!loading && rows.length === 0 && <div className="ops-empty">No published sites found.</div>}

      {rows.length > 0 && (
        <div className="ops-table-wrap">
          <table className="ops-table">
            <thead>
              <tr>
                <th>Slug</th><th>Business</th><th>Status</th>
                <th>Published at</th><th>Snapshot</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r: any) => (
                <tr key={r.tenantId}>
                  <td>
                    <a href={`https://live.bizzness.in/${r.slug}`} target="_blank" rel="noreferrer"
                      style={{ color: "var(--ops-accent)", textDecoration: "none" }}>
                      {r.slug} ↗
                    </a>
                  </td>
                  <td>{r.businessName ?? "—"}</td>
                  <td>{deployBadge(r.deploymentStatus)}</td>
                  <td style={{ fontSize: "0.75rem", color: "var(--ops-muted)" }}>
                    {r.publishedAt ? new Date(r.publishedAt).toLocaleString() : "—"}
                  </td>
                  <td style={{ fontSize: "0.75rem", color: "var(--ops-muted)" }}>
                    {r.snapshotSizeBytes != null
                      ? `${(r.snapshotSizeBytes / 1024).toFixed(1)} KB`
                      : r.snapshotKey ? "exists" : "—"}
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: "0.25rem", flexWrap: "wrap" }}>
                      {(["republish", "invalidate", "rebuild"] as ActionType[]).map(a => (
                        <button key={a} className="ops-btn ops-btn-sm ops-btn-secondary"
                          onClick={() => setConfirm({ tenantId: r.tenantId, slug: r.slug, action: a })}>
                          {a.charAt(0).toUpperCase() + a.slice(1)}
                        </button>
                      ))}
                      <button className="ops-btn ops-btn-sm ops-btn-danger"
                        onClick={() => setConfirm({ tenantId: r.tenantId, slug: r.slug, action: "unpublish" })}>
                        Unpublish
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data?.hasMore && (
        <div className="ops-pagination">
          {page > 1 && <button className="ops-btn ops-btn-sm ops-btn-secondary" onClick={() => setPage(p => p - 1)}>← Prev</button>}
          <span>Page {page}</span>
          <button className="ops-btn ops-btn-sm ops-btn-secondary" onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      )}

      {confirm && (
        <div className="ops-modal-overlay" onClick={() => setConfirm(null)}>
          <div className="ops-modal" onClick={e => e.stopPropagation()}>
            <h2 className="ops-modal-title">
              {confirm.action.charAt(0).toUpperCase() + confirm.action.slice(1)}&nbsp;
              <code style={{ fontSize: "0.875rem" }}>{confirm.slug}</code>
            </h2>
            <p style={{ color: "var(--ops-muted)", fontSize: "0.875rem", marginTop: 0 }}>
              {confirm.action === "republish"  && "Re-trigger the publishing pipeline for this site."}
              {confirm.action === "invalidate" && "Remove this site from the KV cache. It will be re-rendered on next request."}
              {confirm.action === "rebuild"    && "Rebuild HTML from the existing snapshot without changing tenant content."}
              {confirm.action === "unpublish"  && "Take this site offline immediately. Tenant content is not deleted."}
            </p>
            {err && <div className="ops-error">{err}</div>}
            <div className="ops-modal-actions">
              <button className="ops-btn ops-btn-secondary" onClick={() => setConfirm(null)}>Cancel</button>
              <button className={`ops-btn ${confirm.action === "unpublish" ? "ops-btn-danger" : "ops-btn-primary"}`}
                disabled={busy} onClick={doAction}>
                {busy ? "Working…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
